import { useEffect, useState } from "react";
import { GeschuetztesBild, geschuetzteDateiOeffnen } from "../../components/GeschuetztesBild";
import { KundenWerkzeuge } from "../../components/TermineUndDokumente";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Calendar,
  CalendarClock,
  ClipboardList,
  FileText,
  History,
  ImageIcon,
  Mail,
  MapPin,
  MessageSquare,
  Mic,
  Phone,
  Send,
  Sparkles,
  Star,
  Upload,
  User as UserIcon,
  Wrench as WrenchIcon,
} from "lucide-react";
import { loadSession } from "../../lib/auth";
import { api, ApiError } from "../../lib/api";
import { ServiceIcon } from "../../lib/icons";
import {
  DOC_LABELS,
  STATUSES,
  STATUS_LABELS,
  eventLabel,
  statusLabel,
} from "../../lib/labels";

interface AnfrageDetail {
  anfrage: {
    id: string;
    reference_number: string;
    status: string;
    service_category: string;
    sub_service: string | null;
    urgency: string;
    property_type: string | null;
    description: string | null;
    customer_vorname: string;
    customer_nachname: string | null;
    customer_phone: string;
    customer_email: string | null;
    customer_strasse: string | null;
    customer_plz: string | null;
    kunde_typ: string | null;
    quelle?: string | null;
    tracking_token: string;
    assigned_technician: string | null;
    assigned_user_id: string | null;
    inspection_date: string | null;
    scheduled_date: string | null;
    scheduled_duration_minutes: number | null;
    voice_message_path: string | null;
    voice_message_duration_seconds: number | null;
    voice_transcript: string | null;
    voice_transcript_status: "pending" | "done" | "failed" | "skipped" | null;
    followup_sent_at: string | null;
    created_at: string;
  };
  photos: { id: string; original_filename: string | null; created_at: string }[];
  documents: { id: string; doc_type: string; filename: string; created_at: string }[];
  events: {
    event_type: string;
    old_status: string | null;
    new_status: string | null;
    content: string | null;
    visible_to_customer: boolean;
    created_at: string;
  }[];
  rating: { stars: number; comment: string | null; created_at: string } | null;
}


// Wie weit ist der Vorgang? Nur zum Erkennen von Rückschritten.
const STUFEN_RANG: Record<string, number> = {
  received: 0, in_review: 1, inspection_scheduled: 2, inspection_done: 3,
  quote_sent: 4, quote_declined: 4, quote_approved: 5, scheduled: 6,
  in_progress: 7, completed: 8, invoiced: 9, paid: 10,
};

export function AnfrageDetail() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["anfrage", id],
    queryFn: () => api<AnfrageDetail>(`/admin/anfragen/${id}`),
  });

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/admin/anfragen/${id}`, { method: "PATCH", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["anfrage", id] }),
    onError: (e) => {
      alert(e instanceof ApiError ? e.message : "Änderung fehlgeschlagen.");
      qc.invalidateQueries({ queryKey: ["anfrage", id] });
    },
  });

  /**
   * Status über die Auswahlliste ändern. Hier - anders als auf dem Brett -
   * ist ein Rückschritt ausdrücklich erlaubt; er wird nur einmal bestätigt,
   * weil der Kunde dazu erneut eine E-Mail bekommen kann.
   */
  function statusSetzen(neu: string) {
    const alt = data?.anfrage.status;
    if (!alt || neu === alt) return;
    const zurueck = (STUFEN_RANG[neu] ?? 0) < (STUFEN_RANG[alt] ?? 0);
    if (zurueck) {
      const ok = window.confirm(
        `Zurück auf „${STATUS_LABELS[neu] ?? neu}" setzen?\n\n`
        + "Das ist ein Schritt zurück. Der Kunde bekommt dazu unter Umständen erneut eine E-Mail.",
      );
      if (!ok) { qc.invalidateQueries({ queryKey: ["anfrage", id] }); return; }
    }
    patch.mutate({ status: neu, ...(zurueck ? { zurueck: true } : {}) });
  }

  const sendNote = useMutation({
    mutationFn: ({ content, visible }: { content: string; visible: boolean }) =>
      api(`/admin/anfragen/${id}/notes`, {
        method: "POST",
        body: { content, visibleToCustomer: visible },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["anfrage", id] }),
  });

  const sendFollowup = useMutation({
    mutationFn: () =>
      api(`/admin/anfragen/${id}/send-followup`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["anfrage", id] }),
  });

  const notifyOnWay = useMutation({
    mutationFn: () =>
      api(`/admin/anfragen/${id}/notify-on-way`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["anfrage", id] }),
  });

  const uploadDoc = useMutation({
    mutationFn: ({ file, docType }: { file: File; docType: string }) => {
      const fd = new FormData();
      fd.append("docType", docType);
      fd.append("file", file);
      return api(`/admin/anfragen/${id}/documents`, { formData: fd });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["anfrage", id] }),
  });

  const absagen = useMutation({
    mutationFn: (grund: string) =>
      api(`/admin/anfragen/${id}/absagen`, { method: "POST", body: { grund: grund || null } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["anfrage", id] });
      qc.invalidateQueries({ queryKey: ["anfragen"] });
      alert("Anfrage abgesagt. Der Kunde wurde informiert, der Vorgang liegt im Archiv.");
    },
    onError: (e) => alert(e instanceof ApiError ? e.message : "Absagen fehlgeschlagen."),
  });

  function absagenKlick() {
    const grund = window.prompt(
      "Anfrage absagen?\n\nOptionale Begründung für den Kunden (leer lassen = ohne Begründung):",
    );
    if (grund === null) return; // Abbrechen gedrückt
    absagen.mutate(grund.trim());
  }

  const [note, setNote] = useState("");
  const [noteVisible, setNoteVisible] = useState(false);
  const [kontaktOffen, setKontaktOffen] = useState(false);
  const [aiHint, setAiHint] = useState<string | null>(null);

  const suggest = useMutation({
    mutationFn: () =>
      api<{ suggestion: string }>(`/admin/anfragen/${id}/suggest-reply`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      setNote(data.suggestion);
      setNoteVisible(true); // a suggested reply is meant for the customer
      setAiHint(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 501) {
        setAiHint(
          "KI-Vorschläge sind nicht aktiviert (OPENAI_API_KEY fehlt).",
        );
      } else {
        setAiHint("Vorschlag konnte nicht erstellt werden.");
      }
    },
  });

  if (isLoading) return <p className="text-slate-500">Lädt…</p>;
  if (!data) return <p>Nicht gefunden.</p>;
  const a = data.anfrage;

  // Verfolgungs-Link des Kunden - für WhatsApp-Versand und Zwischenablage.
  // Deckt den Fall "Kunde ohne E-Mail" ab (Telefon-Anfragen).
  const trackingLink = `${window.location.origin}/status?anfrage=${a.tracking_token}`;
  function whatsappTeilen() {
    // Deutsche Nummern für wa.me normalisieren: 0171... -> 49171...
    const nummer = a.customer_phone.replace(/[^0-9+]/g, "").replace(/^\+/, "").replace(/^00/, "").replace(/^0/, "49");
    const text = `Guten Tag ${a.customer_vorname}, vielen Dank für Ihre Anfrage (${a.reference_number}). ` +
      `Unter diesem Link sehen Sie jederzeit den aktuellen Stand, können Termine wählen und Angebote ansehen: ${trackingLink}`;
    window.open(`https://wa.me/${nummer}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  }
  async function linkKopieren() {
    try { await navigator.clipboard.writeText(trackingLink); alert("Verfolgungs-Link kopiert."); }
    catch { window.prompt("Link von Hand kopieren:", trackingLink); }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="space-y-5 lg:col-span-2">
        <Link
          to="/app/dashboard"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück
        </Link>
        <div>
          <h1 className="page-title">
            {a.customer_vorname} {a.customer_nachname ?? ""}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span>
              {a.reference_number} ·{" "}
              <span className="text-slate-400">
                eingegangen {new Date(a.created_at).toLocaleString("de-DE")}
              </span>
            </span>
            {a.quelle && a.quelle !== "widget" && (
              <span className="pill bg-sky-50 text-sky-700 ring-1 ring-sky-200">
                {a.quelle === "telefon" ? "per Telefon" : a.quelle === "whatsapp" ? "per WhatsApp" : "persönlich"}
              </span>
            )}
            {a.status !== "cancelled" ? (
              <button
                type="button"
                onClick={absagenKlick}
                disabled={absagen.isPending}
                className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2.5 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
              >
                Anfrage absagen
              </button>
            ) : (
              <span className="pill bg-rose-50 text-rose-700 ring-1 ring-rose-200">Abgesagt</span>
            )}
          </p>
        </div>

        <Section icon={<ClipboardList className="h-3.5 w-3.5" />} title="Anfrage">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
            <ServiceIcon
              id={a.service_category}
              className="h-4 w-4 text-slate-500"
            />
            <span>
              {a.sub_service ?? a.service_category}
            </span>
          </div>
          {a.description && (
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
              {a.description}
            </p>
          )}
        </Section>

        <Section icon={<Calendar className="h-3.5 w-3.5" />} title="Status & Termine">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Status">
              <select
                className="input"
                value={a.status}
                onChange={(e) => statusSetzen(e.target.value)}
              >
                {STATUSES.filter((x) => x !== "cancelled").map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
                {a.status === "cancelled" && (
                  <option value="cancelled">Abgesagt</option>
                )}
              </select>
            </Field>
            <Field label="Zuständiger Mitarbeiter">
              <MitarbeiterWahl
                wert={a.assigned_user_id ?? null}
                anzeigeName={a.assigned_technician ?? null}
                onWahl={(userId) => patch.mutate({ assignedUserId: userId })}
              />
            </Field>
            <Field label="Besichtigung">
              <input
                type="datetime-local"
                className="input"
                defaultValue={a.inspection_date?.slice(0, 16) ?? ""}
                onBlur={(e) =>
                  patch.mutate({
                    inspectionDate: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </Field>
            <Field label="Termin">
              <input
                type="datetime-local"
                className="input"
                defaultValue={a.scheduled_date?.slice(0, 16) ?? ""}
                onBlur={(e) =>
                  patch.mutate({
                    scheduledDate: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </Field>
          </div>

          {a.status === "quote_sent" && (
            <div className="mt-4 flex items-center justify-between rounded-lg bg-violet-50 px-4 py-3 ring-1 ring-violet-200">
              <div className="text-sm text-violet-900">
                {a.followup_sent_at ? (
                  <>
                    Erinnerung gesendet am{" "}
                    <strong>
                      {new Date(a.followup_sent_at).toLocaleDateString("de-DE")}
                    </strong>
                  </>
                ) : (
                  <>Kunde hat noch nicht auf das Angebot reagiert.</>
                )}
              </div>
              <button
                className="btn-secondary btn-sm"
                disabled={sendFollowup.isPending || !a.customer_email}
                onClick={() => sendFollowup.mutate()}
                title={
                  !a.customer_email
                    ? "Kunde hat keine E-Mail-Adresse hinterlegt"
                    : undefined
                }
              >
                <Send className="h-3.5 w-3.5" />
                {a.followup_sent_at ? "Erneut erinnern" : "Erinnerung senden"}
              </button>
            </div>
          )}
          {sendFollowup.isSuccess && (
            <p className="mt-2 text-xs text-emerald-600">
              Erinnerung wurde gesendet.
            </p>
          )}

          {(a.status === "scheduled" || a.status === "in_progress") && (
            <div className="mt-4 flex items-center justify-between rounded-lg bg-teal-50 px-4 py-3 ring-1 ring-teal-200">
              <div className="text-sm text-teal-900">
                Kunde benachrichtigen, dass{" "}
                {a.assigned_technician ? (
                  <strong>{a.assigned_technician}</strong>
                ) : (
                  "der Techniker"
                )}{" "}
                unterwegs ist.
              </div>
              <button
                className="btn-secondary btn-sm"
                disabled={notifyOnWay.isPending || !a.customer_email}
                onClick={() => notifyOnWay.mutate()}
                title={
                  !a.customer_email
                    ? "Kunde hat keine E-Mail-Adresse hinterlegt"
                    : undefined
                }
              >
                <Send className="h-3.5 w-3.5" />
                Techniker unterwegs
              </button>
            </div>
          )}
          {notifyOnWay.isSuccess && (
            <p className="mt-2 text-xs text-emerald-600">
              Kunde wurde benachrichtigt.
            </p>
          )}
        </Section>

        {a.voice_message_path && (
          <Section
            icon={<Mic className="h-3.5 w-3.5" />}
            title={`Sprachnachricht${a.voice_message_duration_seconds ? ` (${formatDuration(a.voice_message_duration_seconds)})` : ""}`}
          >
            <VoicePlayer anfrageId={a.id} />
            {a.voice_transcript && (
              <div className="mt-3 rounded-lg bg-slate-50 p-3">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Automatische Transkription
                </p>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                  {a.voice_transcript}
                </p>
              </div>
            )}
            {a.voice_transcript_status === "pending" && (
              <p className="mt-2 text-xs text-slate-400">
                Transkription läuft…
              </p>
            )}
          </Section>
        )}

        <Section icon={<CalendarClock className="h-3.5 w-3.5" />} title="Für den Kunden">
          <KundenWerkzeuge
            anfrageId={id}
            status={data.anfrage.status}
            /* Anschrift aus der Anfrage - so muss sie für die Rechnung nicht
               noch einmal abgetippt werden. */
            kundeAnschrift={[a.customer_strasse, a.customer_plz].filter(Boolean).join(", ")}
            assignedUserId={a.assigned_user_id}
            onFertig={() => qc.invalidateQueries({ queryKey: ["anfrage", id] })}
          />
          {/* Ohne E-Mail erfährt der Kunde von Terminen und Angeboten nichts -
              dann muss der Link von Hand raus, meist über WhatsApp. */}
          {!a.customer_email && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-medium">Dieser Kunde hat keine E-Mail-Adresse.</p>
              <p className="mt-1">
                Termine, Angebote und Rechnungen werden gespeichert, aber nicht
                verschickt. Schicken Sie den Link nach jedem Schritt selbst:
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={whatsappTeilen}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 font-medium text-white hover:bg-emerald-700"
                >
                  <MessageSquare className="h-3.5 w-3.5" /> Link per WhatsApp senden
                </button>
                <button
                  type="button"
                  onClick={linkKopieren}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-medium text-amber-900 hover:bg-amber-100"
                >
                  Link kopieren
                </button>
                <button
                  type="button"
                  onClick={() => setKontaktOffen(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-medium text-amber-900 hover:bg-amber-100"
                >
                  E-Mail nachtragen
                </button>
              </div>
            </div>
          )}
        </Section>

        <Section icon={<ImageIcon className="h-3.5 w-3.5" />} title="Fotos">
          {data.photos.length === 0 ? (
            <p className="text-sm text-slate-500">Keine Fotos.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {data.photos.map((p) => (
                <div
                  key={p.id}
                  className="group relative block aspect-square cursor-zoom-in overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
                  title={p.original_filename ?? "Kundenfoto"}
                >
                  <GeschuetztesBild
                    fileId={p.id}
                    alt={p.original_filename ?? "Kundenfoto"}
                    className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                    onOeffnen={(url) => window.open(url, "_blank", "noopener")}
                  />
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={<FileText className="h-3.5 w-3.5" />} title="Dokumente">
          <div className="mb-4 flex flex-wrap gap-2">
            {(["angebot", "arbeitsbericht", "rechnung"] as const).map((kind) => (
              <label
                key={kind}
                className="btn-secondary btn-sm cursor-pointer"
              >
                <Upload className="h-3.5 w-3.5" />
                {DOC_LABELS[kind]}
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadDoc.mutate({ file: f, docType: kind });
                    e.target.value = "";
                  }}
                />
              </label>
            ))}
          </div>
          {data.documents.length === 0 ? (
            <p className="text-sm text-slate-500">Noch keine Dokumente.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.documents.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between py-2"
                >
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-slate-400" />
                    <button
                      type="button"
                      onClick={() => geschuetzteDateiOeffnen(d.id)}
                      className="font-medium text-brand hover:underline"
                    >
                      {d.filename}
                    </button>
                    <span className="pill bg-slate-100 text-slate-600">
                      {DOC_LABELS[d.doc_type]}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(d.created_at).toLocaleDateString("de-DE")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section icon={<MessageSquare className="h-3.5 w-3.5" />} title="Notiz / Nachricht">
          <div className="mb-2 flex items-center justify-between">
            <button
              className="btn-ghost btn-sm text-violet-700 hover:bg-violet-50"
              disabled={suggest.isPending}
              onClick={() => suggest.mutate()}
            >
              <Sparkles className="h-3.5 w-3.5" />
              {suggest.isPending ? "Erstelle Vorschlag…" : "KI-Antwortvorschlag"}
            </button>
            {aiHint && <span className="text-xs text-slate-400">{aiHint}</span>}
          </div>
          <textarea
            className="input mb-3 h-32"
            placeholder="Interne Notiz oder Nachricht an Kunde…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
                checked={noteVisible}
                onChange={(e) => setNoteVisible(e.target.checked)}
              />
              An Kunde senden
            </label>
            <button
              className="btn-primary btn-sm"
              disabled={!note.trim() || sendNote.isPending}
              onClick={() => {
                sendNote.mutate({ content: note, visible: noteVisible });
                setNote("");
              }}
            >
              <Send className="h-3.5 w-3.5" />
              Speichern
            </button>
          </div>
        </Section>
      </div>

      <aside className="space-y-5">
        {data.rating && (
          <Section icon={<Star className="h-3.5 w-3.5" />} title="Kundenbewertung">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  className={`h-5 w-5 ${
                    i <= data.rating!.stars
                      ? "fill-amber-400 text-amber-400"
                      : "text-slate-200"
                  }`}
                />
              ))}
              <span className="ml-2 text-sm font-semibold text-slate-900">
                {data.rating.stars}/5
              </span>
            </div>
            {data.rating.comment && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                „{data.rating.comment}"
              </p>
            )}
            <p className="mt-1 text-xs text-slate-400">
              {new Date(data.rating.created_at).toLocaleDateString("de-DE")}
            </p>
          </Section>
        )}

        <Section icon={<UserIcon className="h-3.5 w-3.5" />} title="Kontakt">
          {kontaktOffen ? (
            <KontaktBearbeiten
              anfrage={a}
              speichern={(felder) => {
                patch.mutate(felder);
                setKontaktOffen(false);
              }}
              abbrechen={() => setKontaktOffen(false)}
            />
          ) : (
            <>
              <ContactRow icon={UserIcon} value={`${a.customer_vorname} ${a.customer_nachname ?? ""}`.trim()} />
              <ContactRow icon={Phone} value={a.customer_phone} href={`tel:${a.customer_phone}`} />
              {a.customer_email ? (
                <ContactRow
                  icon={Mail}
                  value={a.customer_email}
                  href={`mailto:${a.customer_email}`}
                />
              ) : (
                <p className="py-1 text-sm text-amber-700">Keine E-Mail hinterlegt</p>
              )}
              {(a.customer_strasse || a.customer_plz) && (
                <ContactRow
                  icon={MapPin}
                  value={[a.customer_strasse, a.customer_plz && `PLZ ${a.customer_plz}`].filter(Boolean).join(", ")}
                />
              )}
              {/* Ein Tippfehler in der E-Mail hieß bisher: der Kunde bekommt
                  nie wieder eine Nachricht, und niemand konnte es richten. */}
              <button
                type="button"
                onClick={() => setKontaktOffen(true)}
                className="mt-1 text-xs text-slate-500 underline underline-offset-2 hover:text-slate-800"
              >
                Kontaktdaten berichtigen
              </button>
            </>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              onClick={whatsappTeilen}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
              title="Öffnet WhatsApp mit einer vorbereiteten Nachricht samt Verfolgungs-Link"
            >
              <MessageSquare className="h-3.5 w-3.5" /> Link per WhatsApp senden
            </button>
            <button
              type="button"
              onClick={linkKopieren}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Verfolgungs-Link kopieren
            </button>
          </div>
          {!a.customer_email && (
            <p className="pt-1 text-xs text-amber-600">
              Ohne E-Mail bekommt der Kunde keine automatischen Status-Mails —
              der WhatsApp-Link ersetzt das.
            </p>
          )}
          {a.kunde_typ && (
            <p className="pt-1">
              <span
                className={
                  "pill " +
                  (a.kunde_typ === "stammkunde"
                    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                    : "bg-slate-100 text-slate-600 ring-1 ring-slate-200")
                }
              >
                {a.kunde_typ === "stammkunde" ? "Stammkunde" : "Neukunde"}
              </span>
            </p>
          )}
        </Section>

        <Section icon={<History className="h-3.5 w-3.5" />} title="Verlauf">
          {data.events.length === 0 ? (
            <p className="text-sm text-slate-500">Noch keine Ereignisse.</p>
          ) : (
            <ul className="space-y-3">
              {data.events.map((e, idx) => (
                <li key={idx} className="border-l-2 border-slate-200 pl-3">
                  <div className="text-xs font-semibold text-slate-800">
                    {e.event_type === "status_change"
                      ? `${statusLabel(e.old_status)} → ${statusLabel(e.new_status)}`
                      : eventLabel(e.event_type)}
                  </div>
                  {e.content && (
                    <div className="mt-0.5 text-xs leading-relaxed text-slate-600">
                      {e.content}
                    </div>
                  )}
                  <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                    {new Date(e.created_at).toLocaleString("de-DE")}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </aside>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="card">
      <h2 className="section-title mb-3">
        <span className="text-slate-400">{icon}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-slate-500">{label}</span>
      {children}
    </label>
  );
}

/**
 * Zuweisung an einen Mitarbeiter des Betriebs. Admins wählen aus der
 * Team-Liste; Mitarbeiter sehen nur, wer zuständig ist (umverteilen darf
 * der Admin). Der Name wandert serverseitig in assigned_technician mit,
 * damit Kunden-Mails und Tracking-Seite ihn weiter anzeigen.
 */
function MitarbeiterWahl({
  wert,
  anzeigeName,
  onWahl,
}: {
  wert: string | null;
  anzeigeName: string | null;
  onWahl: (userId: string | null) => void;
}) {
  const istAdmin = ["admin", "superadmin"].includes(loadSession()?.user.role ?? "");
  const { data } = useQuery({
    queryKey: ["mitarbeiter"],
    queryFn: () =>
      api<{ items: { id: string; name: string; role: string }[] }>("/admin/users"),
    enabled: istAdmin,
    staleTime: 60_000,
  });
  if (!istAdmin) {
    return (
      <p className="input bg-slate-50 text-slate-600">
        {anzeigeName || "Noch niemand zugewiesen"}
      </p>
    );
  }
  return (
    <select
      className="input"
      value={wert ?? ""}
      onChange={(e) => onWahl(e.target.value || null)}
    >
      <option value="">– niemand –</option>
      {(data?.items ?? []).map((m) => (
        <option key={m.id} value={m.id}>
          {m.name}
          {m.role === "admin" ? " (Admin)" : ""}
        </option>
      ))}
    </select>
  );
}

/**
 * Kontaktdaten des Kunden berichtigen.
 *
 * Ein Zahlendreher in der Telefonnummer oder ein Tippfehler in der E-Mail
 * war bisher endgültig: der Kunde bekam nie eine Nachricht, und im Vorgang
 * gab es kein Feld, um es zu ändern. Vorname, Telefon und E-Mail hängen an
 * Mails und Anrede, deshalb bleiben sie Pflicht; Straße und PLZ dürfen leer
 * bleiben (Telefon-Anfragen haben sie oft nicht).
 */
function KontaktBearbeiten({
  anfrage,
  speichern,
  abbrechen,
}: {
  anfrage: {
    customer_vorname: string; customer_nachname: string | null;
    customer_phone: string; customer_email: string | null;
    customer_strasse: string | null; customer_plz: string | null;
  };
  speichern: (felder: Record<string, unknown>) => void;
  abbrechen: () => void;
}) {
  const [vorname, setVorname] = useState(anfrage.customer_vorname ?? "");
  const [nachname, setNachname] = useState(anfrage.customer_nachname ?? "");
  const [telefon, setTelefon] = useState(anfrage.customer_phone ?? "");
  const [email, setEmail] = useState(anfrage.customer_email ?? "");
  const [strasse, setStrasse] = useState(anfrage.customer_strasse ?? "");
  const [plz, setPlz] = useState(anfrage.customer_plz ?? "");
  const [meldung, setMeldung] = useState<string | null>(null);

  function absenden() {
    if (!vorname.trim()) return setMeldung("Bitte einen Vornamen eintragen.");
    if (telefon.trim().length < 3) return setMeldung("Bitte eine Telefonnummer eintragen.");
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return setMeldung("Diese E-Mail-Adresse sieht nicht richtig aus.");
    speichern({
      customerVorname: vorname.trim(),
      customerNachname: nachname.trim(),
      customerPhone: telefon.trim(),
      customerEmail: email.trim(),
      customerStrasse: strasse.trim(),
      customerPlz: plz.trim(),
    });
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input className="input input-sm" placeholder="Vorname" value={vorname} onChange={(e) => setVorname(e.target.value)} />
        <input className="input input-sm" placeholder="Nachname" value={nachname} onChange={(e) => setNachname(e.target.value)} />
      </div>
      <input className="input input-sm" placeholder="Telefon" value={telefon} onChange={(e) => setTelefon(e.target.value)} />
      <input className="input input-sm" placeholder="E-Mail (leer = keine Status-Mails)" value={email} onChange={(e) => setEmail(e.target.value)} />
      <input className="input input-sm" placeholder="Straße und Hausnummer" value={strasse} onChange={(e) => setStrasse(e.target.value)} />
      <input className="input input-sm" placeholder="PLZ" value={plz} onChange={(e) => setPlz(e.target.value)} />
      {meldung && <p className="text-xs text-rose-600">{meldung}</p>}
      <div className="flex gap-2 pt-1">
        <button type="button" className="btn-primary btn-sm" onClick={absenden}>Speichern</button>
        <button type="button" className="btn-ghost btn-sm" onClick={abbrechen}>Abbrechen</button>
      </div>
    </div>
  );
}

function ContactRow({
  icon: Icon,
  value,
  href,
}: {
  icon: typeof Phone;
  value: string;
  href?: string;
}) {
  const content = (
    <span className="flex items-center gap-2 py-1 text-sm text-slate-700 hover:text-slate-900">
      <Icon className="h-4 w-4 text-slate-400" />
      {value}
    </span>
  );
  return href ? <a href={href}>{content}</a> : <div>{content}</div>;
}

function VoicePlayer({ anfrageId }: { anfrageId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    const session = loadSession();
    if (!session) return;

    fetch(`/v1/admin/anfragen/${anfrageId}/voice`, {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("load_failed");
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        revoke = objUrl;
        setUrl(objUrl);
      })
      .catch(() => !cancelled && setError("Wiedergabe nicht möglich."));

    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [anfrageId]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!url) return <p className="text-sm text-slate-500">Lädt…</p>;
  return <audio controls src={url} className="w-full" />;
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

